// Angular
import {APP_INITIALIZER, ErrorHandler, LOCALE_ID, NgModule} from '@angular/core';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {BrowserModule} from '@angular/platform-browser';
import {registerLocaleData} from '@angular/common';
import {HTTP_INTERCEPTORS, HttpClientModule} from '@angular/common/http';
import localeDe from '@angular/common/locales/de';
import localeDeExtra from '@angular/common/locales/extra/de';
// Material
import {MatButtonModule} from '@angular/material/button';
import {MatCardModule} from '@angular/material/card';
import {MatIconModule} from '@angular/material/icon';
import {MatListModule} from '@angular/material/list';
import {MatMenuModule} from '@angular/material/menu';
import {MatSidenavModule} from '@angular/material/sidenav';
import {MatToolbarModule} from '@angular/material/toolbar';
import {MatSnackBarModule} from '@angular/material/snack-bar';
// INFO: Legacy SnackBar Module wird noch von der Splice-Komponente benötigt.
import {MatLegacySnackBarModule} from '@angular/material/legacy-snack-bar';
// Auth
import {
  AuthInterceptor as OidcAuthInterceptor,
  AuthModule,
  OidcSecurityService,
  OpenIdConfiguration,
  StsConfigHttpLoader,
  StsConfigLoader,
} from 'angular-auth-oidc-client';
// Logging
import {LoggerModule, NGXLogger, NgxLoggerLevel} from 'ngx-logger';
// Portal
import {PortalMessageBroker} from './portal/portal-message-broker';
import {PortalTokenService} from './portal/portal-token.service';
// OpenAPI
import {
  ApiModule as BelegApiInternalModule,
  Configuration as BelegConfiguration,
  InhaberService,
} from './openapi/beleg-openapi';
import {ApiModule as ZahlungApiInternalModule, Configuration as ZahlungConfiguration} from './openapi/zahlung-openapi';
// App
import {environment} from '../environments/environment';
import {AppRoutingModule} from './app-routing.module';
import {AppComponent} from './app.component';
import {MockMatIconComponent} from './modules/common/mock-mat-icon/mock-mat-icon.component';
import {httpInterceptorProviders} from './http-interceptors';
import {DefaultComponent} from './templates/default/default.component';
import {IconModule} from './modules/common/icon/icon.module';
import {StoreDevtoolsModule} from '@ngrx/store-devtools';
import {StoreModule} from '@ngrx/store';
import {EffectsModule} from '@ngrx/effects';
import {EditEffects} from './store/effects/edit.effects';
import {MAT_TOOLTIP_DEFAULT_OPTIONS, MatTooltipModule} from '@angular/material/tooltip';
import {mergeMap, take, tap} from 'rxjs';
import {ConfigModule} from './config/config.module';
import {ConfigService} from './config/service/config.service';
import {filter, map} from 'rxjs/operators';
import {AuthInterceptor} from './auth/auth.interceptor';
import {fromPromise} from 'rxjs/internal/observable/innerFrom';
import * as Sentry from '@sentry/angular-ivy';
import jwtDecode, {JwtPayload} from 'jwt-decode';
import {metaReducers, reducers} from './store/reducers/reducers';
import {BelegStipulatedDialogSelectors} from './store/selectors/beleg-stipulated-dialog.selectors';
import {BelegStipulatedDialogActions} from './store/actions/beleg-stipulated-dialog.actions';
import {ReuseBelegDialogSelectors} from './store/selectors/reuse-beleg-dialog.selectors';
import {ReuseBelegDialogActions} from './store/actions/reuse-beleg-dialog.actions';
import {DubletteSnackbarComponent} from './modules/common/dublette-snackbar/dublette-snackbar.component';
import {
  MatomoConsentMode,
  MatomoInitializationMode,
  MatomoInitializerService,
  NgxMatomoTrackerModule,
} from '@ngx-matomo/tracker';
import {ContextInfoService, WellKnownJustFarmingProperties} from './portal/context-info.service';
import {ActivityDisplayComponent} from './modules/activity-display/activity-display.component';
import {MatDialogModule} from '@angular/material/dialog';
import {SentryEffects} from './store/effects/sentry.effects';
import {
  AppSkeletonModule,
  BetriebsauswahlModule,
  CONTEXT_INFO,
  ErrorPagesModule,
  INHABER_SERVICE,
  NavigationModule,
  NGX_LOGGER,
  provideDialogs,
  provideTheme,
} from '@adnova/jf-ng-components';
import {MemoizedSelector} from '@ngrx/store';
import {DubletteSelectors} from './store/selectors/dublette.selectors';
import {DubletteActions} from './store/actions/dublette.actions';
import {PreviewDialogSelectors} from './store/selectors/preview-dialog.selectors';
import {BelegEntitiesActions} from './store/actions/beleg-entities.actions';
import {PreviewDialogEffects} from './store/effects/preview-dialog.effects';
import {StipulateDialogSelectors} from './store/selectors/stipulate-dialog.selectors';
import {StipulateDialogActions} from './store/actions/stipulate-dialog.actions';
import {DubletteNoticeDialogSelectors} from './store/selectors/dublette-notice-dialog.selectors';
import {DubletteNoticeDialogActions} from './store/actions/dublette-notice-dialog.actions';
import {DubletteNoticeDialogEffects} from './store/effects/dublette-notice-dialog.effects';
import {ConfirmDialogSelectors} from './store/selectors/confirm-dialog.selectors';
import {ConfirmDialogActions} from './store/actions/confirm-dialog.actions';
import {NaseDialogSelectors} from './store/selectors/nase-dialog.selectors';
import {NaseDialogActions} from './store/actions/nase-dialog.actions';
import {MitarbeiterDialogSelectors} from './store/selectors/mitarbeiter-dialog.selectors';
import {MitarbeiterDialogActions} from './store/actions/mitarbeiter-dialog.actions';
import {DeleteDialogSelectors} from './store/selectors/delete-dialog.selectors';
import {DeleteDialogActions} from './store/actions/delete-dialog.actions';
import {MoveBelegDialogSelectors} from './store/selectors/move-beleg-dialog.selectors';
import {MoveBelegDialogActions} from './store/actions/move-beleg-dialog.actions';
import {BetriebsauswahlPageComponent} from './modules/pages/betriebsauswahl-page/betriebsauswahl-page.component';
import {DefaultPageComponent} from './modules/pages/default-page/default-page.component';

registerLocaleData(localeDe, 'de-DE', localeDeExtra);

@NgModule({
  declarations: [
    AppComponent,
    MockMatIconComponent,
    DefaultComponent,
    BetriebsauswahlPageComponent,
    DefaultPageComponent,
  ],
  imports: [
    IconModule,
    DubletteSnackbarComponent,
    // Angular
    BrowserAnimationsModule,
    BrowserModule,
    HttpClientModule,
    // Material
    MatIconModule,
    MatListModule,
    MatSidenavModule,
    MatMenuModule,
    MatButtonModule,
    MatToolbarModule,
    MatCardModule,
    MatSnackBarModule,
    // INFO: Legacy SnackBar Module wird noch von der Splice-Komponente benötigt.
    MatLegacySnackBarModule,
    // Config
    ConfigModule,
    // Logging
    LoggerModule.forRoot({
      level: NgxLoggerLevel.INFO, // default logging level
      enableSourceMaps: !environment.production,
    }),
    // Auth
    // TODO: Auth ggf. in eigenes Module auslagern
    AuthModule.forRoot({
      loader: {
        provide: StsConfigLoader,
        useFactory: (configService: ConfigService) => {
          const configObservable = configService.loaded$.pipe(
            take(1),
            map(config => {
              const authConfig: OpenIdConfiguration = {
                // loaded
                authority: config.auth.authority,
                clientId: config.auth.clientId,
                scope: config.auth.scope,
                logLevel: config.auth.logLevel,
                renewTimeBeforeTokenExpiresInSeconds: config.auth.renewTimeBeforeTokenExpiresInSeconds,
                // predefined
                redirectUrl: window.location.origin,
                postLogoutRedirectUri: window.location.origin,
                responseType: 'code',
                silentRenew: true,
                triggerAuthorizationResultEvent: true,
                ignoreNonceAfterRefresh: true,
                useRefreshToken: true,
                // INFO: Bei anpassungen auch AuthInterceptor.matchesSecureRoots() anpassen
                secureRoutes: [config.beleg.apiBaseUrl, config.beleg.naseBaseUrl, config.zahlung.apiBaseUrl],
              };
              return authConfig;
            }),
          );
          return new StsConfigHttpLoader(configObservable);
        },
        deps: [ConfigService],
      },
    }),
    // OpenAPI
    BelegApiInternalModule.forRoot(() => new BelegConfiguration()),
    ZahlungApiInternalModule.forRoot(() => new ZahlungConfiguration()),
    // App
    AppRoutingModule,
    StoreModule.forRoot(reducers, {
      metaReducers,
      runtimeChecks: {
        strictStateImmutability: true,
        strictActionImmutability: true,
        strictActionSerializability: true,
        strictStateSerializability: true,
      },
    }),
    EffectsModule.forRoot([EditEffects, SentryEffects, PreviewDialogEffects, DubletteNoticeDialogEffects]),
    StoreDevtoolsModule.instrument({name: 'Belege Online', maxAge: 25, logOnly: !environment.production}),
    MatTooltipModule,

    NgxMatomoTrackerModule.forRoot({
      mode: MatomoInitializationMode.AUTO_DEFERRED,
      requireConsent: MatomoConsentMode.TRACKING,
    }),
    ActivityDisplayComponent,
    MatDialogModule,
    NavigationModule,
    ErrorPagesModule,
    AppSkeletonModule,
    BetriebsauswahlModule,
  ],
  exports: [AuthModule],
  providers: [
    {
      provide: CONTEXT_INFO,
      useClass: ContextInfoService,
    },
    {
      provide: INHABER_SERVICE,
      useClass: InhaberService,
    },
    {
      provide: NGX_LOGGER,
      useClass: NGXLogger,
    },
    OidcAuthInterceptor,
    {
      // INFO: Interceptor um automatisch da OAuth Token zu setzen
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    {
      // INFO: Initializer das Logging
      provide: APP_INITIALIZER,
      useFactory: (configService: ConfigService, logger: NGXLogger) => () => {
        return configService.loaded$.pipe(
          take(1),
          tap(value => {
            logger.updateConfig({
              ...logger.getConfigSnapshot(),
              level: value.logging.level,
            });
          }),
        );
      },
      deps: [ConfigService, NGXLogger],
      multi: true,
    },
    {
      // INFO: Initializer für Sentry Integration
      provide: APP_INITIALIZER,
      useFactory: (configService: ConfigService, contextInfoService: ContextInfoService, logger: NGXLogger) => () => {
        return configService.loaded$.pipe(
          take(1),
          tap(config => {
            const sentry = config.sentry;
            const info = environment.info;
            if (sentry) {
              Sentry.init({
                enabled: sentry.enabled,
                debug: sentry.debug,
                environment: config.environment,
                release: info.commit,
                dsn: sentry.dsn,
                sampleRate: sentry.sampleRate,
                autoSessionTracking: false,
                enableTracing: false,
                // maxBreadcrumbs nur erhöhen, wenn niemals sensiblen Daten mitgeschrieben werden
                // https://docs.sentry.io/platforms/javascript/guides/angular/configuration/integrations/default/#breadcrumbs
                maxBreadcrumbs: 0,
                initialScope: {
                  extra: {
                    info: info,
                  },
                },
                beforeSend: event => {
                  let contextInfo = contextInfoService?.contextInfo;
                  if (contextInfo?.properties.get(WellKnownJustFarmingProperties.ERROR_REPORTING)) {
                    return event;
                  }
                  logger.debug('Error reporting is disabled, dropping event');
                  return null;
                },
                beforeSendTransaction: event => {
                  let contextInfo = contextInfoService?.contextInfo;
                  if (
                    contextInfo?.properties.get(WellKnownJustFarmingProperties.ANALYTICS) &&
                    contextInfo?.properties.get(WellKnownJustFarmingProperties.ERROR_REPORTING)
                  ) {
                    return event;
                  }
                  logger.debug('Error reporting or analytics are disabled, dropping transaction event');
                  return null;
                },
                beforeBreadcrumb: breadcrumb => {
                  let contextInfo = contextInfoService?.contextInfo;
                  if (
                    contextInfo?.properties.get(WellKnownJustFarmingProperties.ANALYTICS) &&
                    contextInfo?.properties.get(WellKnownJustFarmingProperties.ERROR_REPORTING)
                  ) {
                    return breadcrumb;
                  }
                  logger.debug('Error reporting or analytics are disabled, dropping breadcrumb');
                  return null;
                },
                ignoreErrors: [
                  // INFO: https://gitlab.intern.landdata.de/land-data/belege/belege-online-angular/-/issues/478
                  /startCleanup: Page \d+ is currently rendering/,
                ],
              });
            }
          }),
        );
      },
      deps: [ConfigService, ContextInfoService, NGXLogger],
      multi: true,
    },
    {
      // INFO: Angular Sentry ErrorHandler
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (configService: ConfigService, matomoInitializerService: MatomoInitializerService) => () => {
        return configService.loaded$.pipe(
          take(1),
          tap(config => {
            const matomoConfig = config.matomo;

            if (matomoConfig?.enabled && matomoConfig?.trackerUrl && matomoConfig?.siteId) {
              matomoInitializerService.initializeTracker({
                trackerUrl: matomoConfig.trackerUrl,
                siteId: matomoConfig.siteId,
              });
            }
          }),
        );
      },
      deps: [ConfigService, MatomoInitializerService],
      multi: true,
    },
    {
      // INFO: Initializer der die Base-URL setzt
      provide: APP_INITIALIZER,
      useFactory:
        (
          configService: ConfigService,
          belegConfiguration: BelegConfiguration,
          zahlungConfiguration: ZahlungConfiguration,
        ) =>
        () => {
          return configService.loaded$.pipe(
            take(1),
            tap(value => {
              belegConfiguration.basePath = value.beleg.apiBaseUrl;
              zahlungConfiguration.basePath = value.zahlung.apiBaseUrl;
            }),
          );
        },
      deps: [ConfigService, BelegConfiguration, ZahlungConfiguration],
      multi: true,
    },
    {
      // INFO: Initializer für Portal integration die Base-URL setzt
      provide: APP_INITIALIZER,
      useFactory:
        (
          configService: ConfigService,
          belegConfiguration: BelegConfiguration,
          portalMessageBroker: PortalMessageBroker,
          portalTokenService: PortalTokenService,
          oidcSecurityService: OidcSecurityService,
        ) =>
        () => {
          return configService.loaded$.pipe(
            take(1),
            mergeMap(config => {
              return fromPromise(portalMessageBroker.init(config.portal.targetOrigin)).pipe(map(() => config));
            }),
            mergeMap(config => {
              return fromPromise(portalTokenService.init(config.auth.renewTimeBeforeTokenExpiresInSeconds)).pipe(
                map(() => config),
              );
            }),
            tap(() => {
              const runningEmbedded = portalMessageBroker.isRunningInPortal();
              Sentry.setTag('runningEmbedded', runningEmbedded);

              if (runningEmbedded) {
                const decoded = jwtDecode<JwtPayload>(portalTokenService.getAdnovaToken());
                const token = decoded as any;

                const email = token.email;
                const username = token.preferred_username;
                const name = token.name;

                Sentry.withScope(scope => {
                  let user = scope.getUser();
                  if (!user) {
                    user = {};
                  }
                  user.id = decoded.sub;
                  user.email = email;
                  user.username = username;
                  user['name'] = name;

                  scope.setUser(user);
                });
              } else {
                oidcSecurityService
                  .getAccessToken()
                  .pipe(
                    filter(value => {
                      if (value) {
                        return true;
                      }
                      return false;
                    }),
                  )
                  .subscribe(accessToken => {
                    const decoded = jwtDecode<JwtPayload>(accessToken);
                    const token = decoded as any;

                    const email = token.email;
                    const username = token.preferred_username;
                    const name = token.name;

                    Sentry.withScope(scope => {
                      let user = scope.getUser();
                      if (!user) {
                        user = {};
                      }
                      user.id = decoded.sub;
                      user.email = email;
                      user.username = username;
                      user['name'] = name;

                      scope.setUser(user);
                    });
                  });
              }
            }),
          );
        },
      deps: [ConfigService, BelegConfiguration, PortalMessageBroker, PortalTokenService, OidcSecurityService],
      multi: true,
    },
    {
      provide: LOCALE_ID,
      useValue: 'de-DE',
    },
    {
      provide: MAT_TOOLTIP_DEFAULT_OPTIONS,
      useValue: {
        disableTooltipInteractivity: true,
        showDelay: 600,
        hideDelay: 150,
      },
    },
    provideTheme(),
    httpInterceptorProviders,
    provideDialogs([
      {
        key: 'confirmDialog',
        isOpenSelector: ConfirmDialogSelectors.isConfirmDialogOpen as MemoizedSelector<object, boolean>,
        closeAction: ConfirmDialogActions.close,
        loader: () => import('./modules/confirm-dialog/confirm-dialog.component').then(c => c.ConfirmDialogComponent),
        config: {
          disableClose: false,
          autoFocus: false,
          showCloseButton: false,
        },
      },
      {
        key: 'mitarbeiterDialog',
        isOpenSelector: MitarbeiterDialogSelectors.isMitarbeiterDialogOpen as MemoizedSelector<object, boolean>,
        closeAction: MitarbeiterDialogActions.close,
        loader: () =>
          import('./modules/mitarbeiter-dialog/mitarbeiter-dialog.component').then(c => c.MitarbeiterDialogComponent),
        config: {
          disableClose: false,
          autoFocus: true,
          showCloseButton: true,
          // INFO: Feste Breite, damit Anlegen (langer Hinweistext) und Umbenennen gleich breit sind.
          width: '440px',
        },
      },
      {
        key: 'naseDialog',
        isOpenSelector: NaseDialogSelectors.isNaseDialogOpen as MemoizedSelector<object, boolean>,
        closeAction: NaseDialogActions.close,
        loader: () => import('./modules/nase-dialog/nase-dialog.component').then(c => c.NaseDialogComponent),
        config: {
          disableClose: false,
          autoFocus: false,
          showCloseButton: true,
          width: '591px',
        },
      },
      {
        key: 'deleteDialog',
        isOpenSelector: DeleteDialogSelectors.isDeleteDialogOpen as MemoizedSelector<object, boolean>,
        closeAction: DeleteDialogActions.close,
        loader: () => import('./modules/delete-dialog/delete-dialog.component').then(c => c.DeleteDialogComponent),
        config: {
          disableClose: true,
          autoFocus: false,
          showCloseButton: false,
        },
      },
      {
        key: 'moveBelegDialog',
        isOpenSelector: MoveBelegDialogSelectors.isMoveBelegDialogOpen as MemoizedSelector<object, boolean>,
        closeAction: MoveBelegDialogActions.close,
        loader: () =>
          import('./modules/move-beleg-dialog/move-beleg-dialog.component').then(c => c.MoveBelegDialogComponent),
        config: {
          disableClose: true,
          autoFocus: false,
          showCloseButton: false,
        },
      },
      {
        key: 'reuseBelegDialog',
        isOpenSelector: ReuseBelegDialogSelectors.isReuseBelegDialogOpen as MemoizedSelector<object, boolean>,
        closeAction: ReuseBelegDialogActions.close,
        loader: () =>
          import('./modules/reuse-beleg-dialog/reuse-beleg-dialog.component').then(c => c.ReuseBelegDialogComponent),
        config: {
          disableClose: false,
          autoFocus: false,
          showCloseButton: false,
        },
      },
      {
        key: 'belegStipulatedDialog',
        isOpenSelector: BelegStipulatedDialogSelectors.isBelegStipulatedDialogOpen as MemoizedSelector<object, boolean>,
        closeAction: BelegStipulatedDialogActions.close,
        loader: () =>
          import('./modules/beleg-stipulated-dialog/beleg-stipulated-dialog.component').then(
            c => c.BelegStipulatedDialogComponent,
          ),
        config: {
          disableClose: false,
          autoFocus: false,
          showCloseButton: false,
        },
      },
      {
        key: 'handleDubletteDialog',
        isOpenSelector: DubletteSelectors.isHandleDubletteDialogOpen as MemoizedSelector<object, boolean>,
        closeAction: DubletteActions.closeHandleDubletteDialog,
        loader: () =>
          import('./modules/handle-dublette-dialog/handle-dublette-dialog.component').then(
            c => c.HandleDubletteDialogComponent,
          ),
        config: {
          width: '700px',
          minWidth: '700px',
          autoFocus: false,
          disableClose: true,
          showCloseButton: false,
        },
      },
      {
        key: 'dubletteNoticeDialog',
        isOpenSelector: DubletteNoticeDialogSelectors.isOpen as MemoizedSelector<object, boolean>,
        closeAction: DubletteNoticeDialogActions.close,
        loader: () =>
          import('./modules/dublette-notice-dialog/dublette-notice-dialog.component').then(
            c => c.DubletteNoticeDialogComponent,
          ),
        config: {
          autoFocus: false,
          disableClose: true,
          showCloseButton: true,
        },
      },
      {
        key: 'stipulateDialog',
        isOpenSelector: StipulateDialogSelectors.isOpen as MemoizedSelector<object, boolean>,
        closeAction: StipulateDialogActions.close,
        loader: () =>
          import('./modules/stipulate-dialog/stipulate-dialog.component').then(c => c.StipulateDialogComponent),
        config: {
          autoFocus: false,
          disableClose: true,
          showCloseButton: false,
        },
      },
      {
        key: 'previewDialog',
        isOpenSelector: PreviewDialogSelectors.isOpen as MemoizedSelector<object, boolean>,
        closeAction: BelegEntitiesActions.closePreviewDialog,
        loader: () =>
          import('./modules/pdf-viewer-dialog/pdf-viewer-dialog.component').then(c => c.PdfViewerDialogComponent),
        config: {
          width: '70%',
          height: '85%',
          disableClose: true,
          showCloseButton: true,
        },
      },
    ]),
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
